<template>
  <div class="agency-exchange full-height">
    <!-- ===== 頂列：查詢 / 新增 / 重整 / 灰眼 ===== -->
    <div class="toolbar">
      <el-input
        v-model="listQuery.qry"
        size="mini"
        class="toolbar-search"
        placeholder="搜尋標題..."
        clearable
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-button
        size="mini"
        type="primary"
        icon="el-icon-search"
        @click="handleFilter"
        title="搜尋"
      />
      <el-button
        size="mini"
        type="success"
        icon="el-icon-plus"
        @click="addData"
        title="新增文章"
      />
      <el-button size="mini" icon="el-icon-refresh" @click="refresh" title="重整" />
      <el-button
        size="mini"
        :type="listQuery.onlyMine ? 'warning' : ''"
        icon="el-icon-view"
        @click="toggleOnlyMine"
        title="看自己發過的"
      />
    </div>

    <!-- ===== 標籤篩選 chips（＋新增類別，僅內部） ===== -->
    <div class="tag-bar">
      <span
        class="tag-chip"
        :class="{ active: listQuery.tagId === null }"
        @click="selectTag(null)"
      >
        全部
      </span>
      <span
        v-for="t in enabledTagList"
        :key="t.id"
        class="tag-chip"
        :class="{ active: listQuery.tagId === t.id }"
        :style="chipStyle(t)"
        @click="selectTag(t.id)"
      >
        {{ t.tagName }}
      </span>
      <!-- 僅管理員(內部)可見的新增類別加號 -->
      <el-button
        v-if="isInternal"
        class="tag-add-btn"
        size="mini"
        icon="el-icon-plus"
        circle
        title="新增類別"
        @click="openTagDialog"
      />
      <!-- 僅管理員(內部)可見的刪除類別 -->
      <el-button
        v-if="isInternal"
        class="tag-add-btn"
        size="mini"
        type="danger"
        icon="el-icon-delete"
        circle
        title="刪除類別"
        @click="openTagDeleteDialog"
      />
    </div>

    <!-- ===== 深黃色公告 BAR ===== -->
    <div class="disclaimer-bar">
      <i class="el-icon-warning-outline disclaimer-icon"></i>
      <template v-if="!disclaimerEditing">
        <span class="disclaimer-text">{{ disclaimer || '（尚無公告）' }}</span>
        <el-button
          v-if="isInternal"
          class="disclaimer-btn"
          type="text"
          size="mini"
          icon="el-icon-edit"
          @click="startEditDisclaimer"
        >
          編輯
        </el-button>
      </template>
      <template v-else>
        <el-input
          v-model="disclaimerDraft"
          size="mini"
          class="disclaimer-input"
          type="textarea"
          :autosize="{ minRows: 1, maxRows: 4 }"
          maxlength="500"
        />
        <el-button
          class="disclaimer-btn"
          type="primary"
          size="mini"
          :loading="disclaimerSaving"
          @click="saveDisclaimerText"
        >
          儲存
        </el-button>
        <el-button class="disclaimer-btn" size="mini" @click="cancelEditDisclaimer">取消</el-button>
      </template>
    </div>

    <!-- ===== 卡片牆 ===== -->
    <div v-loading="listLoading" class="card-wall">
      <el-card
        v-for="row in list"
        :key="row.id"
        class="article-card"
        :class="{ 'is-draft': isDraft(row) }"
        :body-style="{ padding: '0px' }"
        shadow="hover"
        @click.native="openDetail(row.id)"
      >
        <div class="card-cover">
          <img :src="coverOf(row)" alt="cover" @error="onCoverError" />
          <span
            v-if="row.tagName"
            class="card-tag"
            :style="{ backgroundColor: row.tagColor || '#909399' }"
          >
            {{ row.tagName }}
          </span>
          <span v-if="isDraft(row)" class="card-draft-badge">尚未發佈</span>
        </div>
        <div class="card-info">
          <div class="card-title" :title="row.title">{{ row.title }}</div>
          <div class="card-footer">
            <div class="card-author">
              <img
                class="card-avatar"
                :src="row.authorAvatar || defaultAvatar"
                @error="onAvatarError"
                alt="avatar"
              />
              <span class="card-author-name">{{ row.authorName || '匿名' }}</span>
            </div>
          </div>
          <div class="card-meta">
            <span class="card-date">{{ formatDate(row.buildDate) }}</span>
            <span class="card-comment">
              <i class="el-icon-chat-dot-round"></i> {{ row.commentCount || 0 }}
            </span>
          </div>
        </div>
      </el-card>

      <div v-if="!listLoading && list.length === 0" class="empty-tip">
        <el-empty description="目前沒有文章"></el-empty>
      </div>
    </div>

    <!-- ===== 分頁 ===== -->
    <pagination
      style="padding-top: 0; margin-top: 10px"
      v-show="total > 0"
      layout="total, prev, pager, next"
      :total="total"
      :page.sync="listQuery.page"
      :limit.sync="listQuery.limit"
      @pagination="getList"
    />

    <!-- ===== 內頁（全屏 dialog） ===== -->
    <el-dialog
      :visible.sync="detailVisible"
      fullscreen
      :show-close="false"
      custom-class="detail-dialog"
      @closed="onDetailClosed"
    >
      <detail
        v-if="detailVisible"
        :id="currentId"
        @edit="onDetailEdit"
        @deleted="onDetailDeleted"
        @changed="getList"
        @close="detailVisible = false"
      />
    </el-dialog>

    <!-- ===== 新增/編輯文章表單 dialog ===== -->
    <el-dialog
      :title="formData.id ? '編輯文章' : '新增文章'"
      :visible.sync="dialogFormVisible"
      width="90%"
      top="2%"
      :close-on-click-modal="false"
      @closed="onFormDialogClosed"
    >
      <el-card class="box-card" v-loading="formLoading">
        <el-form ref="dynamicValidateForm" :model="formData">
          <el-form-item label="封面照" :label-width="formLabelWidth">
            <Upload
              ref="uploadRef"
              :accept="'.jpg,.jpeg,.png'"
              :payload="formData"
              :data-obj="fileObj"
              :limitText="'圖片尺寸建議 420x250，須為 .jpg/.jpeg/.png 格式，單檔不超過 2MB'"
              :limit-file-count="1"
              :use-drag="false"
              :action="'AgencyExchange/saveArticle'"
              :auto-upload="false"
              :show-file-list="true"
              :form-on-loading-parent="formLoading"
              :btn-txt="'上傳封面'"
              :before-file-list="
                formData.fileInfo && formData.fileInfo.length ? formData.fileInfo[0].files : []
              "
              @remove="onCoverRemove"
            />
          </el-form-item>

          <el-form-item
            label="標題"
            :label-width="formLabelWidth"
            prop="title"
            :rules="[{ required: true, message: '請輸入標題', trigger: 'blur' }]"
          >
            <el-input v-model="formData.title" maxlength="100" show-word-limit autocomplete="off" />
          </el-form-item>

          <el-form-item
            label="文章類別"
            :label-width="formLabelWidth"
            prop="tagId"
            :rules="[{ required: true, message: '請選擇文章類別', trigger: 'change' }]"
          >
            <el-select v-model="formData.tagId" placeholder="請選擇文章類別" style="width: 100%">
              <el-option
                v-for="t in formTagOptions"
                :key="t.id"
                :label="t.tagName"
                :value="t.id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="內文" :label-width="formLabelWidth" prop="content">
            <tinymce ref="tinymce" v-model="formData.content" :height="450" :type="29" />
          </el-form-item>

          <el-form-item label="是否公開" :label-width="formLabelWidth">
            <el-switch
              v-model="formData.isPublic"
              active-text="公開"
              inactive-text="草稿"
            />
          </el-form-item>
        </el-form>
      </el-card>

      <div slot="footer" class="dialog-footer">
        <el-button type="primary" :loading="formLoading" @click="saveData('dynamicValidateForm')">
          儲 存
        </el-button>
        <el-button @click="dialogFormVisible = false">取 消</el-button>
      </div>
    </el-dialog>

    <!-- ===== 新增/編輯類別 dialog（僅內部） ===== -->
    <el-dialog title="新增類別" :visible.sync="tagDialogVisible" width="420px">
      <el-form ref="tagForm" :model="tagForm" label-width="80px">
        <el-form-item
          label="類別名稱"
          prop="tagName"
          :rules="[{ required: true, message: '請輸入類別名稱', trigger: 'blur' }]"
        >
          <el-input v-model="tagForm.tagName" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="配色">
          <el-color-picker v-model="tagForm.color" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="tagForm.sort" :min="0" :max="9999" controls-position="right" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button type="primary" :loading="tagSaving" @click="saveTagData">儲 存</el-button>
        <el-button @click="tagDialogVisible = false">取 消</el-button>
      </div>
    </el-dialog>

    <!-- ===== 刪除類別 dialog（僅內部） ===== -->
    <el-dialog title="刪除類別" :visible.sync="tagDeleteDialogVisible" width="420px">
      <p class="tag-delete-hint">
        刪除後的類別不再出現在篩選與發文選單，既有文章的分類顯示不受影響。
      </p>
      <div v-if="enabledTagList.length === 0" class="tag-delete-empty">目前沒有可刪除的類別。</div>
      <ul v-else class="tag-delete-list">
        <li v-for="t in enabledTagList" :key="t.id" class="tag-delete-item">
          <span class="tag-delete-name">
            <i class="tag-delete-dot" :style="{ backgroundColor: t.color || '#409EFF' }"></i>
            {{ t.tagName }}
          </span>
          <el-button
            type="danger"
            size="mini"
            :loading="tagDeletingId === t.id"
            :disabled="tagDeletingId !== null"
            @click="deleteTagData(t)"
          >
            刪除
          </el-button>
        </li>
      </ul>
      <template v-if="disabledTagList.length > 0">
        <p class="tag-delete-hint tag-delete-section">已刪除的類別（可復原）</p>
        <ul class="tag-delete-list">
          <li v-for="t in disabledTagList" :key="t.id" class="tag-delete-item">
            <span class="tag-delete-name is-disabled">
              <i class="tag-delete-dot" :style="{ backgroundColor: t.color || '#409EFF' }"></i>
              {{ t.tagName }}
            </span>
            <el-button
              size="mini"
              :loading="tagDeletingId === t.id"
              :disabled="tagDeletingId !== null"
              @click="restoreTagData(t)"
            >
              復原
            </el-button>
          </li>
        </ul>
      </template>
      <div slot="footer">
        <el-button @click="tagDeleteDialogVisible = false">關 閉</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { Message } from 'element-ui'
import Pagination from '@/components/Pagination'
import Upload from '@/views/ita/components/fileUpload.vue'
import Tinymce from '@/components/Tinymce'
import checkPermission from '@/utils/permission'
import { encodeContentBase64 } from '@/utils/funcs'
import Detail from './detail.vue'
import {
  getArticleList,
  saveArticle,
  getTagList,
  saveTag,
  deleteTag,
  getDisclaimer,
  saveDisclaimer,
} from '@/api/agencyExchange'
import defaultAvatar from '@/assets/images/default_agency_avatar.png'

const INTERNAL_ROLES = ['ITA-ADMIN', 'ITA-DEV', 'ITA-SALES', 'SERVICE-ADMIN']

const defaultFormData = {
  id: null,
  fileInfo: [],
  tagId: null,
  title: '',
  content: '',
  isPublic: true,
  sort: 99,
}

export default {
  name: 'AgencyExchange',
  components: {
    Pagination,
    Upload,
    Tinymce,
    Detail,
  },
  data() {
    return {
      defaultAvatar,
      // 本地佔位圖 (SVG data URI)，離線/防火牆環境也不會壞
      coverPlaceholder:
        'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(
          "<svg xmlns='http://www.w3.org/2000/svg' width='420' height='250'><rect width='100%' height='100%' fill='#f2f3f5'/><text x='50%' y='50%' fill='#c0c4cc' font-family='sans-serif' font-size='22' text-anchor='middle' dominant-baseline='central'>尚無封面</text></svg>"
        ),
      fileObj: { id: 0, type: 'agencyExchange', user: '' },
      // 列表
      list: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        limit: 12,
        sort: null,
        qry: '',
        tagId: null,
        onlyMine: false,
      },
      // 標籤
      tagList: [],
      // 公告
      disclaimer: '',
      disclaimerEditing: false,
      disclaimerDraft: '',
      disclaimerSaving: false,
      // 內頁 dialog
      detailVisible: false,
      currentId: null,
      // 表單 dialog
      dialogFormVisible: false,
      formLoading: false,
      formLabelWidth: '90px',
      formData: JSON.parse(JSON.stringify(defaultFormData)),
      // 類別 dialog
      tagDialogVisible: false,
      tagSaving: false,
      tagForm: { id: null, tagName: '', color: '#409EFF', sort: 0 },
      tagDeleteDialogVisible: false,
      tagDeletingId: null,
    }
  },
  computed: {
    isInternal() {
      return checkPermission(INTERNAL_ROLES)
    },
    enabledTagList() {
      return this.tagList.filter(t => !t.disabled)
    },
    // 已停用(被刪除)的類別，供刪除 dialog 提供復原。
    // 後端 getTagList 只有內部帳號才會回傳停用項(includeDisabled: isInternal)。
    disabledTagList() {
      return this.tagList.filter(t => !!t.disabled)
    },
    // 表單類別下拉：啟用類別 ＋（編輯舊文時）其原類別即使已停用也併入，避免被必填擋住
    formTagOptions() {
      const opts = this.tagList.filter(t => !t.disabled)
      const cur = this.formData.tagId
      if (cur != null && !opts.some(t => t.id === cur)) {
        const found = this.tagList.find(t => t.id === cur)
        if (found) opts.push(found)
      }
      return opts
    },
  },
  async created() {
    await Promise.all([this.getList(), this.getTags(), this.loadDisclaimer()])
  },
  methods: {
    // ---------- 共用 ----------
    coverOf(row) {
      if (row.fileInfo && row.fileInfo.length > 0) {
        const files = row.fileInfo[0].files
        if (files && files.length > 0 && files[0].url) return files[0].url
      }
      return this.coverPlaceholder
    },
    isDraft(item) {
      return !!item && (item.isPublic === false || item.isPublic === 0)
    },
    onCoverError(e) {
      if (!e.target.dataset.fallbackApplied) {
        e.target.dataset.fallbackApplied = '1'
        e.target.src = this.coverPlaceholder
      }
    },
    chipStyle(t) {
      if (this.listQuery.tagId === t.id) {
        const c = t.color || '#409EFF'
        return { backgroundColor: c, borderColor: c, color: '#fff' }
      }
      return { borderColor: t.color || '#dcdfe6', color: t.color || '#606266' }
    },
    onAvatarError(e) {
      if (!e.target.dataset.fallbackApplied) {
        e.target.dataset.fallbackApplied = '1'
        e.target.src = this.defaultAvatar
      }
    },
    formatDate(val) {
      if (!val) return ''
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    // ---------- 列表 ----------
    async getList() {
      this.listLoading = true
      try {
        const resp = await getArticleList(this.listQuery)
        if (resp && resp.success && resp.data) {
          this.list = resp.data.data || []
          this.total = resp.data.total || 0
        } else {
          this.list = []
          this.total = 0
        }
      } catch (e) {
        this.list = []
        this.total = 0
      } finally {
        this.listLoading = false
      }
    },
    handleFilter() {
      this.listQuery.page = 1
      this.getList()
    },
    async refresh() {
      // 重整＝回到初始檢視：清空搜尋字、取消灰眼、標籤退回「全部」，
      // 並一併重載類別與公告(否則其他管理員的異動要按 F5 才看得到)。
      this.listQuery.qry = ''
      this.listQuery.onlyMine = false
      this.listQuery.tagId = null
      this.listQuery.page = 1
      const jobs = [this.getList(), this.getTags()]
      // 公告編輯中就別重載，避免蓋掉管理員打到一半的內容
      if (!this.disclaimerEditing) jobs.push(this.loadDisclaimer())
      await Promise.all(jobs)
    },
    toggleOnlyMine() {
      this.listQuery.onlyMine = !this.listQuery.onlyMine
      this.listQuery.page = 1
      this.getList()
    },
    selectTag(id) {
      this.listQuery.tagId = id
      this.listQuery.page = 1
      this.getList()
    },
    // ---------- 標籤 ----------
    async getTags() {
      try {
        const resp = await getTagList({})
        if (resp && resp.success) {
          this.tagList = resp.data || []
        }
      } catch (e) {
        this.tagList = []
      }
    },
    openTagDialog() {
      this.tagForm = { id: null, tagName: '', color: '#409EFF', sort: 0 }
      this.tagDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.tagForm) this.$refs.tagForm.clearValidate()
      })
    },
    async openTagDeleteDialog() {
      this.tagDeletingId = null
      // 先取最新類別，避免顯示的是頁面載入時的舊快照
      await this.getTags()
      this.tagDeleteDialogVisible = true
    },
    deleteTagData(t) {
      // 刪到只剩一個類別時要特別提醒：類別為發文必填，全刪光就沒人能發新文章。
      const isLast = this.enabledTagList.length <= 1
      const tip = isLast
        ? `「${t.tagName}」是目前最後一個類別，刪除後將無法發表新文章。確定要刪除?`
        : `確定要刪除類別「${t.tagName}」?`
      this.$confirm(tip, '警告', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          this.tagDeletingId = t.id
          try {
            const resp = await deleteTag({ id: t.id })
            if (resp && resp.success) {
              Message({ message: '類別已刪除', type: 'success', duration: 3000 })
              await this.getTags()
              // 被刪掉的類別若正是目前的篩選條件，退回「全部」。
              // (列表 SQL 只有 AND a.TagID = @tagId，不 join 類別表也不看 Disabled，
              //  所以文章仍查得到；問題是該 chip 已從 tag bar 消失，
              //  會變成「沒有任何 chip 亮起卻仍在篩選」的孤兒狀態。)
              if (this.listQuery.tagId === t.id) {
                this.listQuery.tagId = null
                this.listQuery.page = 1
                await this.getList()
              }
            }
          } finally {
            this.tagDeletingId = null
          }
        })
        .catch(() => {})
    },
    async restoreTagData(t) {
      // 復原＝把 Disabled 設回 false。後端 SaveTag 會整列覆寫，
      // 故名稱/配色/排序都要原值帶回，否則會被清成預設值。
      this.tagDeletingId = t.id
      try {
        const resp = await saveTag({
          id: t.id,
          tagName: t.tagName,
          color: t.color,
          sort: t.sort,
          disabled: false,
        })
        if (resp && resp.success) {
          Message({ message: '類別已復原', type: 'success', duration: 3000 })
          await this.getTags()
        }
      } finally {
        this.tagDeletingId = null
      }
    },
    saveTagData() {
      this.$refs.tagForm.validate(async valid => {
        if (!valid) return
        this.tagSaving = true
        try {
          const resp = await saveTag(this.tagForm)
          if (resp && resp.success) {
            Message({ message: '類別已儲存', type: 'success', duration: 3000 })
            this.tagDialogVisible = false
            await this.getTags()
          }
        } finally {
          this.tagSaving = false
        }
      })
    },
    // ---------- 公告 ----------
    async loadDisclaimer() {
      try {
        const resp = await getDisclaimer()
        if (resp && resp.success) {
          this.disclaimer = resp.data || ''
        }
      } catch (e) {
        this.disclaimer = ''
      }
    },
    startEditDisclaimer() {
      this.disclaimerDraft = this.disclaimer
      this.disclaimerEditing = true
    },
    cancelEditDisclaimer() {
      this.disclaimerEditing = false
    },
    async saveDisclaimerText() {
      this.disclaimerSaving = true
      try {
        // 公告文字 base64 編碼，避免 AWS WAF 誤擋 403；後端 SaveDisclaimer 會先 Base64ContentCodec.Decode 還原
        const resp = await saveDisclaimer({ disclaimerText: encodeContentBase64(this.disclaimerDraft) })
        if (resp && resp.success) {
          this.disclaimer = this.disclaimerDraft
          this.disclaimerEditing = false
          Message({ message: '公告已更新', type: 'success', duration: 3000 })
        }
      } finally {
        this.disclaimerSaving = false
      }
    },
    // ---------- 內頁 ----------
    openDetail(id) {
      this.currentId = id
      this.detailVisible = true
    },
    onDetailClosed() {
      this.currentId = null
    },
    onDetailDeleted() {
      this.detailVisible = false
      this.getList()
    },
    onDetailEdit(article) {
      this.detailVisible = false
      this.$nextTick(() => {
        this.editData(article)
      })
    },
    // ---------- 表單 ----------
    addData() {
      this.formData = JSON.parse(JSON.stringify(defaultFormData))
      this.dialogFormVisible = true
      this.$nextTick(() => {
        this.resetFormRefs('')
      })
    },
    editData(article) {
      // article 來自 getArticle：含 content / fileInfo / tagId / title / isPublic
      const jData = JSON.parse(JSON.stringify(article))
      this.formData = {
        id: jData.id,
        fileInfo: jData.fileInfo || [],
        tagId: jData.tagId,
        title: jData.title || '',
        content: jData.content || '',
        isPublic: jData.isPublic !== false && jData.isPublic !== 0,
        sort: jData.sort != null ? jData.sort : 99,
      }
      this.dialogFormVisible = true
      this.$nextTick(() => {
        this.resetFormRefs(this.formData.content)
      })
    },
    resetFormRefs(content) {
      if (this.$refs.dynamicValidateForm) this.$refs.dynamicValidateForm.clearValidate()
      if (this.$refs.tinymce) {
        try {
          this.$refs.tinymce.setValue(content || '')
        } catch (e) {
          // tinymce 尚未 init，v-model 會於 init callback 帶入內容
        }
      }
    },
    async saveData(formName) {
      const refUpload = this.$refs['uploadRef']
      this.$refs[formName].validate(async valid => {
        if (!valid) return
        const payload = await refUpload.combineFileAndPayload()
        if (!payload) {
          Message.error('檔案繫結表單發生未知錯誤，無法儲存資料。')
          return
        }
        // 封面必填：新檔或既有檔至少要有一個
        const hasNewFile =
          payload.fileInfo && payload.fileInfo.length > 0 && payload.fileInfo[0].files.length > 0
        const hasOldFile = payload.fileIDs && payload.fileIDs.length > 0
        if (!hasNewFile && !hasOldFile) {
          Message.error('請上傳封面照')
          return
        }
        // 前端把關單檔 ≤2MB
        if (!this.checkCoverSize(refUpload)) return

        this.formLoading = true
        try {
          // 標題與富文本內文 base64 編碼，避免 AWS WAF 將 HTML 誤判 XSS 回 403；
          // 後端 AgencyExchangeService.SaveArticle 會先 Base64ContentCodec.Decode 還原再消毒/存檔。
          // 務必用複製物件送出：payload 是 combineFileAndPayload 回傳的 formData 參照，
          // 直接賦值會污染響應式資料 → TinyMCE 的 value watcher 把 "b64:xxx" 當純文字
          // 塞回編輯器並包成 <p>b64:xxx</p>，再存一次就變二次編碼；後端只 Decode 一層
          // (且前綴被 <p> 擋住而完全不解碼)，最後 base64 原文就會被存進 DB。
          const resp = await saveArticle({
            ...payload,
            title: encodeContentBase64(payload.title),
            content: encodeContentBase64(payload.content),
          })
          if (resp && resp.success) {
            Message({ message: '資料處理完成', type: 'success', duration: 3000 })
            this.dialogFormVisible = false
            await this.getList()
          }
        } finally {
          this.formLoading = false
        }
      })
    },
    checkCoverSize(refUpload) {
      const files = (refUpload && refUpload.fileList) || []
      for (const f of files) {
        if (f.raw && f.size && f.size / 1024 / 1024 > 2) {
          Message.error('封面照大小不可超過 2MB')
          return false
        }
      }
      return true
    },
    onCoverRemove() {
      // 移除既有封面 → 同步清掉 formData.fileInfo，避免 before-file-list 又帶回
      this.formData.fileInfo = []
    },
    onFormDialogClosed() {
      const refUpload = this.$refs['uploadRef']
      if (refUpload) refUpload.clearFiles()
      if (this.$refs['dynamicValidateForm']) this.$refs['dynamicValidateForm'].clearValidate()
      if (this.$refs['tinymce']) {
        try {
          this.$refs['tinymce'].setValue('')
        } catch (e) {
          // ignore
        }
      }
      this.formData = JSON.parse(JSON.stringify(defaultFormData))
    },
  },
}
</script>

<style lang="scss" scoped>
.agency-exchange {
  padding: 16px 20px;
  box-sizing: border-box;
  height: 100%;
  overflow: auto;
}

/* 頂列 */
.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.toolbar-search {
  width: 240px;
}

/* 標籤 chips */
.tag-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.tag-chip {
  display: inline-block;
  padding: 3px 14px;
  font-size: 13px;
  line-height: 20px;
  border: 1px solid #dcdfe6;
  border-radius: 16px;
  color: #606266;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  background: #fff;
}
.tag-chip:hover {
  opacity: 0.85;
}
.tag-chip.active {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}
.tag-add-btn {
  margin-left: 4px;
}

/* 公告 BAR */
.disclaimer-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  background: #fdf6d3;
  border: 1px solid #f5e08a;
  border-left: 4px solid #e6a23c;
  color: #8a6d3b;
  padding: 8px 14px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
}
.disclaimer-icon {
  color: #e6a23c;
  font-size: 16px;
}
.disclaimer-text {
  flex: 1;
  white-space: pre-wrap;
  word-break: break-word;
  font-weight: 700;
}
.disclaimer-input {
  flex: 1;
  min-width: 200px;
}
.tag-delete-hint {
  margin: 0 0 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.tag-delete-empty {
  padding: 12px 0;
  color: #909399;
  text-align: center;
}
.tag-delete-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 320px;
  overflow-y: auto;
}
.tag-delete-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #ebeef5;
}
.tag-delete-item:last-child {
  border-bottom: none;
}
.tag-delete-name {
  display: flex;
  align-items: center;
  gap: 8px;
  word-break: break-word;
}
.tag-delete-name.is-disabled {
  color: #c0c4cc;
  text-decoration: line-through;
}
.tag-delete-section {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}
.tag-delete-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.disclaimer-btn {
  color: #8a6d3b;
}

/* 卡片牆 */
.card-wall {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  min-height: 200px;
}
.article-card {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.15s;
}
.article-card:hover {
  transform: translateY(-2px);
}
.article-card.is-draft {
  filter: grayscale(1);
}
.card-cover {
  position: relative;
  width: 100%;
  height: 180px;
  background: #f2f3f5;
}
.card-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.card-tag {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 10px;
  font-size: 12px;
  color: #fff;
  border-radius: 12px;
}
.card-draft-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 2px 10px;
  font-size: 12px;
  color: #fff;
  background: rgba(96, 98, 102, 0.85);
  border-radius: 12px;
}
.card-info {
  padding: 12px 14px 14px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
  height: 42px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}
.card-footer {
  margin-top: 10px;
}
.card-author {
  display: flex;
  align-items: center;
}
.card-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  background: #f2f3f5;
  margin-right: 8px;
  flex-shrink: 0;
}
.card-author-name {
  font-size: 13px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}
.card-comment i {
  margin-right: 2px;
}
.empty-tip {
  grid-column: 1 / -1;
}
</style>

<style>
.detail-dialog .el-dialog__header {
  padding: 0;
}
.detail-dialog .el-dialog__body {
  padding: 0;
}
</style>
